import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Card, Table, Button, Input, Select, Tag, Space, Modal,
  notification, Row, Col, Statistic, Drawer, Form, Upload, Divider, Descriptions
} from 'antd';
import {
  SearchOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ReloadOutlined, BookOutlined, UserOutlined, PhoneOutlined, EnvironmentOutlined,
  FileTextOutlined, PlusOutlined, UploadOutlined, LoadingOutlined, DollarOutlined,
  ClockCircleOutlined, TeamOutlined, TrophyOutlined, DownloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const { Option } = Select;
const { TextArea } = Input;

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const STATUS_COLOR = {
  pending:      'orange',
  under_review: 'blue',
  approved:     'green',
  rejected:     'red',
  disbursed:    'purple',
};
const STATUS_LABEL = {
  pending:      '⏳ प्रतीक्षारत',
  under_review: '🔍 समीक्षाधीन',
  approved:     '✅ स्वीकृत',
  rejected:     '❌ अस्वीकृत',
  disbursed:    '💸 वितरित',
};
const SUPPORT_LABELS = {
  scholarship: 'छात्रवृत्ति',
  fees:        'फीस सहायता',
  books:       'पुस्तकें / स्टेशनरी',
  uniform:     'वर्दी',
  medical:     'चिकित्सा',
  other:       'अन्य',
};

const inrFmt = a => a ? `₹${Number(a).toLocaleString('en-IN')}` : '—';
const fmtDate = d => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

export default function StudentSupportList() {
  const [data,        setData]        = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [search,      setSearch]      = useState('');
  const [statusF,     setStatusF]     = useState('');
  const [typeF,       setTypeF]       = useState('');
  const [stats,       setStats]       = useState({});

  // view drawer
  const [viewItem,    setViewItem]    = useState(null);
  const [viewDetail,  setViewDetail]  = useState(null);
  const [detailLoad,  setDetailLoad]  = useState(false);

  // review drawer
  const [reviewItem,  setReviewItem]  = useState(null);
  const [reviewForm,  setReviewForm]  = useState({ status: '', committee_note: '', approved_amount: '', disbursed_note: '' });
  const [reviewing,   setReviewing]   = useState(false);

  // sample form upload
  const [sampleFormUrl,  setSampleFormUrl]  = useState(null);
  const [sampleUploading, setSampleUploading] = useState(false);
  const sampleFileRef = useRef(null);

  // office-apply drawer
  const [officeOpen,  setOfficeOpen]  = useState(false);
  const [officeForm,  setOfficeForm]  = useState({
    office_staff: '', student_name: '', student_class: '', school_name: '',
    school_address: '', father_name: '', mother_name: '', family_income: '',
    family_members: '', village: '', district: '', state: '', phone_no: '',
    support_type: 'scholarship', amount_requested: '', reason: '',
  });
  const [officeFile,  setOfficeFile]  = useState(null);
  const [submitting,  setSubmitting]  = useState(false);
  const officeDocRef = useRef(null);

  const fetchSampleFormUrl = useCallback(async () => {
    try {
      const r = await axios.get(`${baseUrl}/admin/student-support/sample-form-url`, { headers: auth() });
      if (r.data.status) setSampleFormUrl(r.data.url || null);
    } catch {}
  }, []);

  const uploadSampleForm = async e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { notification.warning({ message: 'File must be under 10MB', placement: 'topRight' }); return; }
    setSampleUploading(true);
    try {
      const fd = new FormData();
      fd.append('sample_form', file);
      const r = await axios.post(`${baseUrl}/admin/student-support/sample-form`, fd, {
        headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
      });
      if (r.data.status) {
        setSampleFormUrl(r.data.url);
        notification.success({ message: 'Sample form uploaded! Members can now download it.', placement: 'topRight' });
      }
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Upload failed', placement: 'topRight' });
    } finally { setSampleUploading(false); }
  };

  const removeSampleForm = async () => {
    Modal.confirm({
      title: 'Remove custom sample form?',
      content: 'Members will see the default generated form.',
      okType: 'danger',
      onOk: async () => {
        try {
          await axios.delete(`${baseUrl}/admin/student-support/sample-form`, { headers: auth() });
          setSampleFormUrl(null);
          notification.success({ message: 'Custom form removed.', placement: 'topRight' });
        } catch { notification.error({ message: 'Failed', placement: 'topRight' }); }
      },
    });
  };

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${baseUrl}/admin/student-support/stats`, { headers: auth() });
      if (r.data.status) setStats(r.data.result || {});
    } catch {}
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: 30 });
      if (search)  p.append('search',  search);
      if (statusF) p.append('status',  statusF);
      if (typeF)   p.append('type',    typeF);
      const r = await axios.get(`${baseUrl}/admin/student-support/list?${p}`, { headers: auth() });
      if (r.data.status) { setData(r.data.result || []); setTotal(r.data.total || 0); }
    } catch {
      notification.error({ message: 'Failed to load', placement: 'topRight' });
    } finally { setLoading(false); }
  }, [page, search, statusF, typeF]);

  useEffect(() => { fetchData(); fetchStats(); fetchSampleFormUrl(); }, [fetchData, fetchStats, fetchSampleFormUrl]);

  const openDetail = async row => {
    setViewItem(row);
    setDetailLoad(true);
    try {
      const r = await axios.get(`${baseUrl}/admin/student-support/${row.id}`, { headers: auth() });
      if (r.data.status) setViewDetail(r.data.result);
    } catch {} finally { setDetailLoad(false); }
  };

  const openReview = row => {
    setReviewItem(row);
    setReviewForm({
      status: row.status || 'under_review',
      committee_note: row.committee_note || '',
      approved_amount: row.approved_amount || '',
      disbursed_note: '',
    });
  };

  const submitReview = async () => {
    if (!reviewForm.status) { notification.warning({ message: 'Status चुनें', placement: 'topRight' }); return; }
    setReviewing(true);
    try {
      const payload = {
        status:           reviewForm.status,
        committee_note:   reviewForm.committee_note || null,
        reviewed_by:      localStorage.getItem('staffName') || localStorage.getItem('name') || 'Admin',
      };
      if (reviewForm.status === 'approved' && reviewForm.approved_amount)
        payload.approved_amount = parseFloat(reviewForm.approved_amount);
      if (reviewForm.status === 'disbursed' && reviewForm.disbursed_note)
        payload.disbursed_note = reviewForm.disbursed_note;

      await axios.patch(`${baseUrl}/admin/student-support/${reviewItem.id}/review`, payload, { headers: auth() });
      notification.success({ message: 'Application updated', placement: 'topRight' });
      setReviewItem(null);
      fetchData(); fetchStats();
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Failed', placement: 'topRight' });
    } finally { setReviewing(false); }
  };

  const submitOfficeApply = async () => {
    if (!officeFile)                 { notification.error({ message: 'Document अपलोड करें', placement: 'topRight' }); return; }
    if (!officeForm.student_name)    { notification.warning({ message: 'विद्यार्थी का नाम जरूरी है', placement: 'topRight' }); return; }
    if (!officeForm.reason?.trim())  { notification.warning({ message: 'कारण जरूरी है', placement: 'topRight' }); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('application_doc', officeFile);
      Object.entries(officeForm).forEach(([k, v]) => { if (v) fd.append(k, v); });
      await axios.post(`${baseUrl}/admin/student-support/office-apply`, fd, {
        headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
      });
      notification.success({ message: 'Walk-in application recorded', placement: 'topRight' });
      setOfficeOpen(false);
      setOfficeFile(null);
      setOfficeForm({ office_staff:'', student_name:'', student_class:'', school_name:'', school_address:'', father_name:'', mother_name:'', family_income:'', family_members:'', village:'', district:'', state:'', phone_no:'', support_type:'scholarship', amount_requested:'', reason:'' });
      fetchData(); fetchStats();
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Submit failed', placement: 'topRight' });
    } finally { setSubmitting(false); }
  };

  const handleDelete = id => {
    Modal.confirm({
      title: 'Delete this application?',
      icon: <CloseCircleOutlined style={{ color: 'red' }} />,
      okType: 'danger',
      onOk: async () => {
        try {
          await axios.delete(`${baseUrl}/admin/student-support/${id}`, { headers: auth() });
          notification.success({ message: 'Deleted', placement: 'topRight' });
          fetchData(); fetchStats();
        } catch { notification.error({ message: 'Delete failed', placement: 'topRight' }); }
      },
    });
  };

  const columns = [
    {
      title: '#', dataIndex: 'id', key: 'id', width: 60, align: 'center',
      render: (v, _, i) => <span style={{ color: '#6b7280', fontSize: 12 }}>{(page - 1) * 30 + i + 1}</span>,
    },
    {
      title: 'विद्यार्थी', dataIndex: 'student_name', key: 'student_name',
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 700, color: '#111827' }}>{v}</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>{r.student_class || ''}{r.student_class && r.school_name ? ' · ' : ''}{r.school_name || ''}</div>
          {r.applied_via === 'office' && <Tag color="cyan" style={{ fontSize: 10, marginTop: 2 }}>Walk-in</Tag>}
        </div>
      ),
    },
    {
      title: 'सहायता', dataIndex: 'support_type', key: 'support_type', width: 120,
      render: v => <Tag color="#7a0000" style={{ color: '#fff', fontSize: 11 }}>{SUPPORT_LABELS[v] || v}</Tag>,
    },
    {
      title: 'राशि', key: 'amount', width: 110,
      render: r => (
        <div style={{ fontSize: 12 }}>
          <div>आवेदित: {inrFmt(r.amount_requested)}</div>
          {r.approved_amount && <div style={{ color: '#15803d', fontWeight: 700 }}>स्वीकृत: {inrFmt(r.approved_amount)}</div>}
        </div>
      ),
    },
    {
      title: 'स्थान', key: 'location', width: 120,
      render: r => <span style={{ fontSize: 11, color: '#6b7280' }}>{[r.village, r.district].filter(Boolean).join(', ') || '—'}</span>,
    },
    {
      title: 'मोबाइल', dataIndex: 'phone_no', key: 'phone_no', width: 110,
      render: v => v ? <span style={{ fontSize: 12 }}>{v}</span> : '—',
    },
    {
      title: 'स्थिति', dataIndex: 'status', key: 'status', width: 130,
      render: v => <Tag color={STATUS_COLOR[v]} style={{ fontSize: 11 }}>{STATUS_LABEL[v] || v}</Tag>,
    },
    {
      title: 'दिनांक', dataIndex: 'created_at', key: 'created_at', width: 100,
      render: v => <span style={{ fontSize: 11, color: '#9ca3af' }}>{fmtDate(v)}</span>,
    },
    {
      title: 'Actions', key: 'actions', width: 140, fixed: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(r)} title="View Details" />
          <Button size="small" type="primary" style={{ background: '#7a0000', borderColor: '#7a0000' }} icon={<CheckCircleOutlined />} onClick={() => openReview(r)} title="Review" />
          {r.application_doc && (
            <Button size="small" icon={<FileTextOutlined />} href={r.application_doc} target="_blank" title="View Document" />
          )}
          <Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => handleDelete(r.id)} title="Delete" />
        </Space>
      ),
    },
  ];

  const iS = { width: '100%', padding: '7px 10px', border: '1.5px solid #d1d5db', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const lS = { fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 3, display: 'block', marginTop: 8 };
  const OF = k => e => setOfficeForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div style={{ padding: '0 0 40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, color: '#7a0000', fontSize: 20, fontWeight: 800 }}>
            <BookOutlined style={{ marginRight: 8 }} /> शिक्षा सहायता — Admin Panel
          </h2>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Student Support Applications Management</div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ background: '#7a0000', borderColor: '#7a0000' }}
          onClick={() => setOfficeOpen(true)}
        >
          Walk-in Application
        </Button>
      </div>

      {/* Sample Form Upload */}
      <Card size="small" style={{ marginBottom: 16, borderLeft: '4px solid #7a0000' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 13, color: '#7a0000', marginBottom: 4 }}>
              <DownloadOutlined style={{ marginRight: 6 }} /> Sample Application Form (नमूना पत्र)
            </div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>
              {sampleFormUrl
                ? 'Custom form uploaded — members will download this file.'
                : 'No custom form uploaded — members see auto-generated HTML form.'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {sampleFormUrl && (
              <>
                <Button
                  size="small"
                  icon={<FileTextOutlined />}
                  href={sampleFormUrl}
                  target="_blank"
                  style={{ fontSize: 12 }}
                >
                  View Current Form
                </Button>
                <Button size="small" danger onClick={removeSampleForm} style={{ fontSize: 12 }}>
                  Remove
                </Button>
              </>
            )}
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#7a0000', color: '#fff', borderRadius: 6,
              padding: '4px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              opacity: sampleUploading ? 0.7 : 1,
            }}>
              {sampleUploading ? <LoadingOutlined /> : <UploadOutlined />}
              {sampleUploading ? 'Uploading…' : sampleFormUrl ? 'Replace Form' : 'Upload Sample Form'}
              <input
                ref={sampleFileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                style={{ display: 'none' }}
                onChange={uploadSampleForm}
                disabled={sampleUploading}
              />
            </label>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {[
          { key: 'total',        label: 'कुल आवेदन',    icon: <TeamOutlined />,         color: '#6b7280' },
          { key: 'pending',      label: 'प्रतीक्षारत',  icon: <ClockCircleOutlined />,  color: '#d97706' },
          { key: 'under_review', label: 'समीक्षाधीन',   icon: <EyeOutlined />,          color: '#1d4ed8' },
          { key: 'approved',     label: 'स्वीकृत',       icon: <CheckCircleOutlined />,  color: '#15803d' },
          { key: 'rejected',     label: 'अस्वीकृत',     icon: <CloseCircleOutlined />,  color: '#b91c1c' },
          { key: 'disbursed',    label: 'वितरित',        icon: <TrophyOutlined />,       color: '#7c3aed' },
        ].map(s => (
          <Col xs={8} sm={4} key={s.key}>
            <Card size="small" style={{ textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{stats[s.key] || 0}</div>
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{s.label}</div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filter Bar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            placeholder="विद्यार्थी / विद्यालय / गाँव खोजें..."
            prefix={<SearchOutlined />}
            style={{ width: 240 }}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            allowClear
          />
          <Select placeholder="स्थिति" style={{ width: 150 }} value={statusF || undefined} onChange={v => { setStatusF(v || ''); setPage(1); }} allowClear>
            {Object.entries(STATUS_LABEL).map(([v, l]) => <Option key={v} value={v}>{l}</Option>)}
          </Select>
          <Select placeholder="सहायता प्रकार" style={{ width: 160 }} value={typeF || undefined} onChange={v => { setTypeF(v || ''); setPage(1); }} allowClear>
            {Object.entries(SUPPORT_LABELS).map(([v, l]) => <Option key={v} value={v}>{l}</Option>)}
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => { setSearch(''); setStatusF(''); setTypeF(''); setPage(1); fetchData(); fetchStats(); }}>
            Reset
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Card size="small">
        <Table
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{
            current: page,
            total,
            pageSize: 30,
            onChange: p => setPage(p),
            showTotal: (t, r) => `${r[0]}-${r[1]} of ${t} applications`,
            showSizeChanger: false,
          }}
          size="small"
        />
      </Card>

      {/* ── View Detail Drawer ── */}
      <Drawer
        title={viewItem ? `${viewItem.student_name} — विवरण` : 'Application Detail'}
        width={500}
        open={!!viewItem}
        onClose={() => { setViewItem(null); setViewDetail(null); }}
      >
        {detailLoad && <div style={{ textAlign: 'center', padding: 40 }}><LoadingOutlined style={{ fontSize: 28 }} /></div>}
        {!detailLoad && viewDetail && (
          <div>
            <div style={{ marginBottom: 10 }}>
              <Tag color={STATUS_COLOR[viewDetail.status]}>{STATUS_LABEL[viewDetail.status] || viewDetail.status}</Tag>
              <Tag color="#7a0000" style={{ color: '#fff' }}>{SUPPORT_LABELS[viewDetail.support_type] || viewDetail.support_type}</Tag>
              {viewDetail.applied_via === 'office' && <Tag color="cyan">Walk-in</Tag>}
            </div>

            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="विद्यार्थी" span={2}><strong>{viewDetail.student_name}</strong></Descriptions.Item>
              <Descriptions.Item label="जन्म तिथि">{fmtDate(viewDetail.student_dob)}</Descriptions.Item>
              <Descriptions.Item label="लिंग">{viewDetail.student_gender || '—'}</Descriptions.Item>
              <Descriptions.Item label="कक्षा">{viewDetail.student_class || '—'}</Descriptions.Item>
              <Descriptions.Item label="विद्यालय">{viewDetail.school_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="पिता" span={2}>{viewDetail.father_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="माता">{viewDetail.mother_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="अभिभावक">{viewDetail.guardian_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="वार्षिक आय">{inrFmt(viewDetail.family_income)}</Descriptions.Item>
              <Descriptions.Item label="सदस्य संख्या">{viewDetail.family_members || '—'}</Descriptions.Item>
              <Descriptions.Item label="मोबाइल">{viewDetail.phone_no || viewDetail.member_phone || '—'}</Descriptions.Item>
              <Descriptions.Item label="गाँव / जिला">{[viewDetail.village, viewDetail.district, viewDetail.state].filter(Boolean).join(', ') || '—'}</Descriptions.Item>
              <Descriptions.Item label="आवेदित राशि">{inrFmt(viewDetail.amount_requested)}</Descriptions.Item>
              <Descriptions.Item label="स्वीकृत राशि">{inrFmt(viewDetail.approved_amount)}</Descriptions.Item>
              <Descriptions.Item label="आवेदन दिनांक" span={2}>{fmtDate(viewDetail.created_at)}</Descriptions.Item>
            </Descriptions>

            <Divider>कारण / Reason</Divider>
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 13, lineHeight: 1.7 }}>
              {viewDetail.reason}
            </div>

            {viewDetail.committee_note && (
              <>
                <Divider>समिति का निर्णय</Divider>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{viewDetail.reviewed_by || '—'} • {fmtDate(viewDetail.reviewed_at)}</div>
                  {viewDetail.committee_note}
                </div>
              </>
            )}

            {viewDetail.disbursed_at && (
              <>
                <Divider>वितरण</Divider>
                <div style={{ background: '#faf5ff', border: '1px solid #c4b5fd', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
                  <div>दिनांक: {fmtDate(viewDetail.disbursed_at)}</div>
                  {viewDetail.disbursed_note && <div style={{ marginTop: 4 }}>{viewDetail.disbursed_note}</div>}
                </div>
              </>
            )}

            {viewDetail.application_doc && (
              <>
                <Divider>दस्तावेज़</Divider>
                <Button icon={<FileTextOutlined />} href={viewDetail.application_doc} target="_blank" block>
                  आवेदन पत्र देखें / डाउनलोड करें
                </Button>
              </>
            )}

            <Divider />
            <Button
              type="primary"
              block
              style={{ background: '#7a0000', borderColor: '#7a0000' }}
              icon={<CheckCircleOutlined />}
              onClick={() => { setViewItem(null); setViewDetail(null); openReview(viewDetail); }}
            >
              इस आवेदन को Review करें
            </Button>
          </div>
        )}
      </Drawer>

      {/* ── Review Drawer ── */}
      <Drawer
        title={reviewItem ? `Review: ${reviewItem.student_name}` : 'Review Application'}
        width={420}
        open={!!reviewItem}
        onClose={() => setReviewItem(null)}
        footer={
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => setReviewItem(null)}>Cancel</Button>
            <Button
              type="primary"
              loading={reviewing}
              style={{ background: '#7a0000', borderColor: '#7a0000' }}
              onClick={submitReview}
            >
              Update Status
            </Button>
          </div>
        }
      >
        {reviewItem && (
          <div>
            <div style={{ background: '#f9fafb', borderRadius: 10, padding: '10px 13px', marginBottom: 16, fontSize: 12, color: '#374151' }}>
              <strong>{reviewItem.student_name}</strong> — {SUPPORT_LABELS[reviewItem.support_type] || reviewItem.support_type}
              <br />{reviewItem.school_name || ''}{reviewItem.village ? ` · ${reviewItem.village}` : ''}
              <br />आवेदित: {inrFmt(reviewItem.amount_requested)} · वर्तमान स्थिति: <Tag color={STATUS_COLOR[reviewItem.status]} style={{ fontSize: 10 }}>{STATUS_LABEL[reviewItem.status]}</Tag>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, display: 'block' }}>नई स्थिति / New Status *</label>
              <Select
                style={{ width: '100%' }}
                value={reviewForm.status}
                onChange={v => setReviewForm(f => ({ ...f, status: v }))}
              >
                {Object.entries(STATUS_LABEL).map(([v, l]) => <Option key={v} value={v}>{l}</Option>)}
              </Select>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, display: 'block' }}>समिति का नोट / Committee Note</label>
              <TextArea
                rows={4}
                placeholder="निर्णय का कारण, टिप्पणी, या आवेदक के लिए संदेश…"
                value={reviewForm.committee_note}
                onChange={e => setReviewForm(f => ({ ...f, committee_note: e.target.value }))}
              />
            </div>

            {reviewForm.status === 'approved' && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, display: 'block' }}>
                  <DollarOutlined /> स्वीकृत राशि (₹) / Approved Amount
                </label>
                <Input
                  type="number"
                  placeholder="जैसे: 5000"
                  value={reviewForm.approved_amount}
                  onChange={e => setReviewForm(f => ({ ...f, approved_amount: e.target.value }))}
                />
              </div>
            )}

            {reviewForm.status === 'disbursed' && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, display: 'block' }}>वितरण नोट / Disbursement Note</label>
                <TextArea
                  rows={3}
                  placeholder="वितरण का विवरण (चेक नंबर, UPI, आदि)…"
                  value={reviewForm.disbursed_note}
                  onChange={e => setReviewForm(f => ({ ...f, disbursed_note: e.target.value }))}
                />
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* ── Office / Walk-in Apply Drawer ── */}
      <Drawer
        title="Walk-in Application (Office Entry)"
        width={500}
        open={officeOpen}
        onClose={() => { setOfficeOpen(false); setOfficeFile(null); }}
        footer={
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => { setOfficeOpen(false); setOfficeFile(null); }}>Cancel</Button>
            <Button
              type="primary"
              loading={submitting}
              style={{ background: '#7a0000', borderColor: '#7a0000' }}
              onClick={submitOfficeApply}
            >
              Submit Application
            </Button>
          </div>
        }
      >
        <div>
          {/* Document upload */}
          <div style={{ background: officeFile ? '#f0fdf4' : '#fef2f2', border: `2px dashed ${officeFile ? '#86efac' : '#fca5a5'}`, borderRadius: 10, padding: '14px', marginBottom: 14, textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: officeFile ? '#15803d' : '#b91c1c', marginBottom: 6 }}>
              {officeFile ? `✅ ${officeFile.name}` : '⚠️ आवेदन पत्र अपलोड करें (अनिवार्य)'}
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: officeFile ? '#15803d' : '#7a0000', color: '#fff', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              <UploadOutlined /> {officeFile ? 'बदलें' : 'Document चुनें'}
              <input ref={officeDocRef} type="file" accept="image/*,.pdf,.doc,.docx" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setOfficeFile(f); }} />
            </label>
          </div>

          {/* Staff name */}
          <label style={lS}>Staff Name (who filled)</label>
          <input style={iS} placeholder="Staff name" value={officeForm.office_staff} onChange={OF('office_staff')} />

          {/* Section: Student */}
          <Divider orientation="left" style={{ fontSize: 12, color: '#7a0000', margin: '12px 0 6px' }}>विद्यार्थी की जानकारी</Divider>
          <label style={lS}>विद्यार्थी का नाम *</label>
          <input style={iS} placeholder="पूरा नाम" value={officeForm.student_name} onChange={OF('student_name')} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
            <div><label style={lS}>कक्षा / Course</label><input style={iS} placeholder="जैसे: 10th, B.A." value={officeForm.student_class} onChange={OF('student_class')} /></div>
            <div><label style={lS}>विद्यालय</label><input style={iS} placeholder="School name" value={officeForm.school_name} onChange={OF('school_name')} /></div>
          </div>
          <label style={lS}>विद्यालय पता</label>
          <input style={iS} placeholder="School address" value={officeForm.school_address} onChange={OF('school_address')} />

          {/* Section: Family */}
          <Divider orientation="left" style={{ fontSize: 12, color: '#7a0000', margin: '12px 0 6px' }}>परिवार की जानकारी</Divider>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
            <div><label style={lS}>पिता का नाम</label><input style={iS} value={officeForm.father_name} onChange={OF('father_name')} /></div>
            <div><label style={lS}>माता का नाम</label><input style={iS} value={officeForm.mother_name} onChange={OF('mother_name')} /></div>
            <div><label style={lS}>वार्षिक आय (₹)</label><input style={iS} type="number" value={officeForm.family_income} onChange={OF('family_income')} /></div>
            <div><label style={lS}>परिवार सदस्य</label><input style={iS} type="number" value={officeForm.family_members} onChange={OF('family_members')} /></div>
            <div><label style={lS}>मोबाइल</label><input style={iS} type="tel" value={officeForm.phone_no} onChange={OF('phone_no')} /></div>
            <div><label style={lS}>गाँव / शहर</label><input style={iS} value={officeForm.village} onChange={OF('village')} /></div>
            <div><label style={lS}>जिला</label><input style={iS} value={officeForm.district} onChange={OF('district')} /></div>
            <div><label style={lS}>राज्य</label><input style={iS} value={officeForm.state} onChange={OF('state')} /></div>
          </div>

          {/* Section: Support */}
          <Divider orientation="left" style={{ fontSize: 12, color: '#7a0000', margin: '12px 0 6px' }}>सहायता का विवरण</Divider>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
            <div>
              <label style={lS}>सहायता प्रकार *</label>
              <select style={iS} value={officeForm.support_type} onChange={OF('support_type')}>
                {Object.entries(SUPPORT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div><label style={lS}>आवश्यक राशि (₹)</label><input style={iS} type="number" value={officeForm.amount_requested} onChange={OF('amount_requested')} /></div>
          </div>
          <label style={lS}>कारण *</label>
          <textarea style={{ ...iS, height: 80, resize: 'vertical' }} placeholder="विस्तार से बताएं…" value={officeForm.reason} onChange={OF('reason')} />
        </div>
      </Drawer>
    </div>
  );
}
